import { supabase, supabaseUrl, supabaseAnon } from './supabase';

const SESSION_KEY = 'future-beacon-analytics-session';
const INSTALL_KEY = 'future-beacon-analytics-install';
const LOCAL_EVENTS_KEY = 'future-beacon-battle-events';

export interface BattleEvent {
  id: string;
  session_id: string;
  event_name: string;
  payload: Record<string, unknown>;
  created_at: string;
}

function makeId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function battleSessionId(): string {
  const existing = localStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const id = makeId();
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

export function analyticsInstallId(): string {
  const existing = localStorage.getItem(INSTALL_KEY);
  if (existing) return existing;
  const id = makeId();
  localStorage.setItem(INSTALL_KEY, id);
  return id;
}

export function startBattleSession(): string {
  const id = makeId();
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

function localEvents(): BattleEvent[] {
  try {
    const raw = localStorage.getItem(LOCAL_EVENTS_KEY);
    return raw ? JSON.parse(raw) as BattleEvent[] : [];
  } catch {
    return [];
  }
}

function storeLocal(event: BattleEvent): void {
  const events = localEvents();
  events.push(event);
  localStorage.setItem(LOCAL_EVENTS_KEY, JSON.stringify(events.slice(-500)));
}

function upsertGameSession(id: string, patch: Record<string, unknown>): void {
  if (!supabase) return;
  supabase
    .from('game_sessions')
    .upsert({ id, ...patch }, { onConflict: 'id' })
    .then(({ error }) => {
      if (error) console.error('[analytics] session upsert failed:', error.message);
    });
}

export function trackBattleEvent(eventName: string, payload: Record<string, unknown> = {}): void {
  const event: BattleEvent = {
    id: makeId(),
    session_id: battleSessionId(),
    event_name: eventName,
    payload: {
      ...payload,
      installId: analyticsInstallId(),
    },
    created_at: new Date().toISOString(),
  };

  storeLocal(event);
  if (!supabase) return;

  supabase.from('battle_events').insert({
    session_id: event.session_id,
    event_name: event.event_name,
    payload: event.payload,
    created_at: event.created_at,
  }).then(({ error }) => {
    if (error) console.error('[analytics] insert failed:', error.message, error.code);
  });

  // Mirror key lifecycle events to game_sessions for efficient admin analytics
  if (eventName === 'session_started') {
    upsertGameSession(event.session_id, {
      map_code:   payload.mapCode   != null ? String(payload.mapCode)   : null,
      map_seed:   payload.mapSeed   != null ? Number(payload.mapSeed)   : null,
      map_width:  payload.mapWidth  != null ? Number(payload.mapWidth)  : null,
      map_height: payload.mapHeight != null ? Number(payload.mapHeight) : null,
      started_at: event.created_at,
    });
  } else if (eventName === 'game_over') {
    upsertGameSession(event.session_id, {
      ended_at:               event.created_at,
      duration_seconds:       payload.timeSurvived    != null ? Math.round(Number(payload.timeSurvived))   : null,
      waves_repelled:         payload.wavesRepelled   != null ? Number(payload.wavesRepelled)              : null,
      enemies_defeated:       payload.enemiesDefeated != null ? Number(payload.enemiesDefeated)            : null,
      venues_claimed:         payload.venuesClaimed   != null ? Number(payload.venuesClaimed)              : null,
      song_drops:             payload.songDrops       != null ? Number(payload.songDrops)                  : null,
      scene_score:            payload.sceneScore      != null ? Number(payload.sceneScore)                 : null,
      scene_tier:             payload.sceneTier       != null ? String(payload.sceneTier)                  : null,
      corporate_hq_destroyed: payload.corporateHQDestroyed === true,
      outcome:                payload.outcome != null
        ? String(payload.outcome)
        : payload.beaconOwner === 'player' || payload.beaconOwner === 'good' ? 'survived' : 'lost',
    });
  }
}

/** Try inserting a single test row and return null on success or an error message. */
export async function testBattleEventInsert(): Promise<string | null> {
  if (!supabase) return 'Supabase not configured (env vars missing from build)';
  const { error } = await supabase.from('battle_events').insert({
    session_id: 'admin-test',
    event_name: 'admin_test',
    payload: { source: 'Battle Centre test write' },
  });
  return error ? `${error.message} (code: ${error.code})` : null;
}

export type BattleEventsResult = {
  events: BattleEvent[];
  source: 'supabase' | 'local';
  /** Non-null when Supabase is configured but the query failed (e.g. table missing). */
  supabaseError?: string;
};

export async function loadBattleEvents(limit = 250): Promise<BattleEventsResult> {
  if (supabase) {
    const { data, error } = await supabase
      .from('battle_events')
      .select('id, session_id, event_name, payload, created_at')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (!error && data) return { events: data as BattleEvent[], source: 'supabase' };
    return {
      events: localEvents().slice(-limit).reverse(),
      source: 'local',
      supabaseError: error?.message ?? 'Unknown Supabase error',
    };
  }

  return { events: localEvents().slice(-limit).reverse(), source: 'local' };
}

export type GameSession = {
  id: string;
  map_code: string | null;
  map_seed: number | null;
  map_width: number | null;
  map_height: number | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  waves_repelled: number | null;
  enemies_defeated: number | null;
  venues_claimed: number | null;
  song_drops: number | null;
  scene_score: number | null;
  scene_tier: string | null;
  corporate_hq_destroyed: boolean | null;
  outcome: string | null;
};

export type GameSessionsResult = {
  sessions: GameSession[];
  error?: string;
};

export async function loadGameSessions(limit = 500): Promise<GameSessionsResult> {
  if (!supabase) return { sessions: [] };
  const { data, error } = await supabase
    .from('game_sessions')
    .select('*')
    .order('started_at', { ascending: false })
    .limit(limit);
  if (error) return { sessions: [], error: error.message };
  return { sessions: (data ?? []) as GameSession[] };
}

// ── Unit position tracking ─────────────────────────────────────────────────

export type UnitPositionSample = {
  unit_role: string;
  col: number;
  row: number;
  elapsed_seconds: number;
  wave: number;
};

const PENDING_POSITIONS_KEY = 'future-beacon-pending-positions';

type PendingPositions = { sessionId: string; samples: UnitPositionSample[] };

function sendPositionsNow(sessionId: string, samples: UnitPositionSample[]): void {
  if (!supabaseUrl || !supabaseAnon || samples.length === 0) return;
  const rows = samples.map(s => ({ session_id: sessionId, ...s }));
  fetch(`${supabaseUrl}/rest/v1/unit_positions`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'apikey':        supabaseAnon,
      'Content-Type':  'application/json',
      'Prefer':        'return=minimal,resolution=ignore-duplicates',
    },
    body: JSON.stringify(rows),
  }).catch(err => console.error('[analytics] unit_positions flush failed:', err));
}

/** Save positions to localStorage then fire the network request.
 *  The localStorage copy survives an immediate iOS Safari reload (where
 *  keepalive fetch is unreliable); flushPendingPositions() clears it on
 *  the next game start once it confirms a send was attempted. */
export function flushUnitPositions(sessionId: string, samples: UnitPositionSample[]): void {
  if (samples.length === 0) return;
  try {
    localStorage.setItem(PENDING_POSITIONS_KEY, JSON.stringify({ sessionId, samples }));
  } catch { /* storage full — best effort */ }
  sendPositionsNow(sessionId, samples);
}

/** Called at the start of each new game. Resends any positions that were saved
 *  to localStorage but may not have made it to Supabase (e.g. iOS reload). */
export function flushPendingPositions(): void {
  try {
    const raw = localStorage.getItem(PENDING_POSITIONS_KEY);
    if (!raw) return;
    localStorage.removeItem(PENDING_POSITIONS_KEY);
    const { sessionId, samples } = JSON.parse(raw) as PendingPositions;
    if (sessionId && Array.isArray(samples) && samples.length > 0) {
      sendPositionsNow(sessionId, samples);
    }
  } catch { /* corrupt entry — discard silently */ }
}

/** Load all position samples for a session (admin console). */
export async function loadUnitPositions(sessionId: string): Promise<UnitPositionSample[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('unit_positions')
    .select('unit_role, col, row, elapsed_seconds, wave')
    .eq('session_id', sessionId)
    .order('elapsed_seconds', { ascending: true });
  if (error || !data) return [];
  return data as UnitPositionSample[];
}
